import { useRef, useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Pagination,
  PaginationItem,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import SearchOffRounded from "@mui/icons-material/SearchOffRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import {
  TransactionsTable,
  Loading,
  EmptyState,
  PageHeader,
  TransactionCard,
} from "../components";
import CustomSnackbar, {
  CustomSnackbarRef,
} from "../components/CustomSnackbar";
import ExportDialog from "../components/ExportDialog";
import { useTransactions } from "../hooks/useTransaction";
import { useMedia } from "../context/MediaContext";

const TransactionPage: React.FC = () => {
  const snackbarRef = useRef<CustomSnackbarRef>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const { isMobile600 } = useMedia();
  const {
    transactions,
    total,
    loading,
    error,
    fetchRecentTrans,
    fetchTransactions,
    page,
    setPage,
    totalPage,
  } = useTransactions(snackbarRef);
  const [filterss, setFilters] = useState({ search_query: "" });

  const runSearch = () => {
    setPage(1);
    if (filterss.search_query.trim() !== "") {
      fetchTransactions(filterss);
    } else {
      fetchRecentTrans(1);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        paddingTop: "25px",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        // Layout hands the page a container with overflow: hidden. Without
        // minHeight: 0 this column refuses to shrink and whatever does not fit
        // is clipped — the pagination first, since it comes last.
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "96%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          margin: "0 auto",
          paddingBottom: 1.5,
        }}
      >
        <PageHeader
          title="Transactions"
          description="Track your payments and payouts in real time"
          icon={ReceiptLongOutlined}
          actions={
            <>
              <Button
                variant="contained"
                startIcon={<DownloadRounded />}
                onClick={() => setExportOpen(true)}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshRounded />}
                disabled={loading}
                onClick={() => {
                  setFilters({ search_query: "" });
                  setPage(1);
                  fetchRecentTrans(1);
                }}
              >
                Refresh
              </Button>
            </>
          }
        />

        <Box
          component="form"
          role="search"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            runSearch();
          }}
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
          }}
        >
          <TextField
            label="Search by transaction or order ID"
            name="search_query"
            fullWidth
            value={filterss.search_query}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search_query: e.target.value }))
            }
            slotProps={{
              htmlInput: {
                style: {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              },
              input: {
                endAdornment: filterss.search_query ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      size="small"
                      onClick={() => setFilters({ search_query: "" })}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              height: "56px",
              flexShrink: 0,
              whiteSpace: "nowrap",
              minWidth: { sm: 180 },
            }}
          >
            {filterss.search_query !== "" ? "Search" : "Show latest"}
          </Button>
        </Box>

        <Box
          sx={{
            // Takes the remaining height instead of a slice of the viewport:
            // the old 64vh stacked with the header and search box and pushed
            // the pagination past the bottom edge.
            flex: 1,
            minHeight: 0,
            marginTop: "25px",
            borderRadius: "15px",
            overflowY: isMobile600 ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
          {loading ? (
            <Loading compact variant="table" />
          ) : transactions.length > 0 && isMobile600 ? (
            <Box display="flex" flexDirection="column" gap={1}>
              {transactions.map((t) => (
                <TransactionCard key={t.id} t={t} />
              ))}
            </Box>
          ) : transactions.length > 0 ? (
            <TransactionsTable transactions={transactions} />
          ) : (
            <EmptyState
              icon={SearchOffRounded}
              title="No transactions found"
              description={
                error ||
                "Enter a transaction or order ID above, or load the latest operations."
              }
              compact
              action={
                <Button
                  variant="contained"
                  onClick={() => {
                    setPage(1);
                    fetchRecentTrans(1);
                  }}
                >
                  Show latest
                </Button>
              }
            />
          )}
        </Box>

        {total > 20 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: isMobile600 ? "center" : "start",
              mt: 2,
              // Pagination never shrinks: it gets its space before the list.
              flexShrink: 0,
            }}
          >
            <Pagination
              count={totalPage}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              variant="outlined"
              shape="rounded"
              size="large"
              siblingCount={1}
              boundaryCount={isMobile600 ? 0 : 1}
              renderItem={(item) => {
                if (
                  item.type === "start-ellipsis" ||
                  item.type === "end-ellipsis"
                ) {
                  return null;
                }
                return <PaginationItem {...item} />;
              }}
              sx={{ "& .MuiPaginationItem-root": { fontSize: "15px" } }}
            />
          </Box>
        ) : null}
      </Box>

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onNotify={(message, severity) =>
          snackbarRef.current?.showSnackbar(message, severity)
        }
      />
      <CustomSnackbar ref={snackbarRef} />
    </Box>
  );
};

export default TransactionPage;
